import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDropzone } from 'react-dropzone';

import { makeStyles } from '@material-ui/styles';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import Box from '@material-ui/core/Box';
import Paper from '@material-ui/core/Paper';
import { styled, Typography } from '@material-ui/core';
import Table from '@material-ui/core/Table';
import TableContainer from '@material-ui/core/TableContainer';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import TableHead from '@material-ui/core/TableHead';
import TableBody from '@material-ui/core/TableBody';

import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import ImageIcon from '@material-ui/icons/Image';
import EditIcon from '@material-ui/icons/Edit';
import SaveIcon from '@material-ui/icons/Save';
import AddAPhotoIcon from '@material-ui/icons/AddAPhoto';

import ToggleButton from '../../components/toggleButton';
import VariableTextField from '../../components/variableTextField';
import Dialog from '../../components/dialog/base';
import { useAPI } from '../../utils/api';
import { MODE } from '../../utils/mode';

const useStyles = makeStyles({
  root: {
    '&:last-of-type td': {
      borderBottom: 'none',
    },
  },
});

const CellItemContainer = styled(Grid)({
  textAlign: 'center',
  borderLeft: '1px solid',
});

const CellItemGrid = styled(Grid)({
  padding: 6,
  borderBottom: '1px solid',
  borderRight: '1px solid',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflowWrap: 'anywhere',
});

const columns = [
  { id: 'order', label: '版位位置', minWidth: '80px' },
  { id: 'title', label: '活動名稱', minWidth: '180px' },
  {
    id: 'link',
    label: '連結網址',
    minWidth: '240px',
  },
  {
    id: 'pictureName',
    label: '目前圖檔',
    minWidth: '180px',
  },
  {
    id: 'function',
    label: '功能',
    minWidth: '180px',
  },
];

const toggleDisplay = () => {};

const createData = ({
  id,
  order,
  title,
  link,
  pictureString,
  pictureName,
  display,
}) => {
  return {
    id,
    order,
    title,
    link,
    pictureString,
    pictureName,
    display,
    toggleDisplay,
  };
};

const UploadedImagePreview = ({ image, onDelete, isReadOnly }) => {
  return image ? (
    <>
      <Typography variant="body2" mb={1}>
        圖片預覽
      </Typography>
      <Box
        sx={{
          width: 'fit-content',
          borderRadius: 2,
          border: '1px solid #eaeaea',
          padding: 1,
          boxSizing: 'border-box',
        }}
        onClick={isReadOnly ? undefined : () => onDelete()}
      >
        <img
          style={{
            maxHeight: '240px',
          }}
          src={typeof image === 'string' ? image : image.preview}
          title="首頁蓋板"
        />
      </Box>
      {isReadOnly ? undefined : (
        <Typography variant="body2" mb={1}>
          (點擊照片以刪除)
        </Typography>
      )}
    </>
  ) : (
    ''
  );
};

UploadedImagePreview.propTypes = {
  image: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  onDelete: PropTypes.func,
  isReadOnly: PropTypes.bool,
};

UploadedImagePreview.defaultProps = {
  onDelete: () => {},
  isReadOnly: false,
};

const HomepageConfigPage = () => {
  const classes = useStyles();
  const api = useAPI();

  const configForm = useRef();
  const uploader = useRef();

  const [isUnmounted, setIsUnmounted] = useState(false);
  const [mode, setMode] = useState(MODE.VIEW);
  const [configs, setConfigs] = useState([]);
  const [importedFiles, setImportedFiles] = useState();
  const [uploadErrorMessage, setUploadErrorMessage] = useState('');
  const [shouldPreviewUpdate, setShouldPreviewUpdate] = useState(false);
  const [uploadImageConfigOrder, setUploadImageConfigOrder] = useState();
  const [shouldPictureNameUpdate, setShouldPictureNameUpdate] = useState(false);
  const [shouldPageUpdate, setShouldPageUpdate] = useState(false);

  const onDrop = useCallback((acceptedFiles, fileRejections) => {
    if (fileRejections.length === 0) {
      // Success
      setUploadErrorMessage('');
      setImportedFiles(
        acceptedFiles.map((file) =>
          Object.assign(file, {
            preview: URL.createObjectURL(file),
          })
        )
      );
    } else if (fileRejections.length > 1) {
      setImportedFiles();
      setUploadErrorMessage('一次只能上傳一個檔案');
    } else {
      setImportedFiles();
      const rejectedFile = fileRejections[0];
      const rejectedReasons = rejectedFile.errors;
      const invalidTypeError = rejectedReasons.some(
        (error) => error.code === 'file-invalid-type'
      );
      if (invalidTypeError) {
        setUploadErrorMessage('只可接受圖片');
      } else {
        setUploadErrorMessage('上傳檔案時發生錯誤');
      }
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    maxFiles: 1,
    accept: 'image/*',
  });

  const deleteStoredImage = () => {
    setImportedFiles();
    if (!configs) return;
    const newData = configs;
    newData.pictureName = '';
    newData.pictureString = '';
    setConfigs(newData);
    setShouldPreviewUpdate(true);
  };

  const fetchConfigs = () => {
    api
      .getHomepagePictureConfigs()
      .then((configData) => {
        if (!isUnmounted) {
          const rows = [];

          configData = configData.slice(0, 5);

          for (const [index, config] of configData.entries()) {
            rows.push(
              createData({
                id: config.id,
                order: index + 1,
                title: config.activityName,
                link: config.connectedWebsite,
                pictureString: config.pictureString,
                pictureName: config.pictureName,
                display: config.display,
              })
            );
          }

          setConfigs(rows);
          setShouldPageUpdate(true);
        }
      })
      .catch(() => {});
  };

  const onSubmit = (event) => {
    event.preventDefault();

    if (mode === MODE.EDIT) {
      setMode(MODE.VIEW);

      const elements = event.target.querySelectorAll('td');
      const newConfigs = JSON.parse(JSON.stringify(configs));

      for (const element of elements) {
        if (element.offsetHeight > 0) {
          for (const config of newConfigs) {
            const title = element.querySelector(
              `input[name="title-${config.id}"]`
            );
            if (title) config.title = title.value;

            const link = element.querySelector(
              `input[name="link-${config.id}"]`
            );
            if (link) config.link = link.value;

            const display = element.querySelector(
              `input[name="is-display-${config.id}"]`
            );
            if (display)
              config.display = display.checked
                ? display.value === 'display'
                : false;
          }
        }
      }

      const data = newConfigs;

      api
        .saveHomepagePictureConfigs({ data })
        .then((isSuccess) => {
          if (isSuccess) fetchConfigs();
        })
        .catch(() => {});
      return;
    } else setMode(MODE.EDIT);
  };

  const clearUploadStatus = () => {
    setUploadImageConfigOrder();
    setUploadErrorMessage('');
    setImportedFiles();
  };

  const updateUploadData = (order) => {
    if (!importedFiles || importedFiles.length !== 1) {
      setUploadErrorMessage('您尚未上傳圖片');
      return;
    }

    const image = importedFiles[0];

    const newConfigs = JSON.parse(JSON.stringify(configs));

    const reader = new FileReader();
    reader.onloadend = () => {
      const pictureName = image.name;
      const pictureString = reader.result;

      newConfigs[order - 1].pictureName = pictureName;
      newConfigs[order - 1].pictureString = pictureString;

      setUploadImageConfigOrder();
      setConfigs(newConfigs);
      setShouldPictureNameUpdate(true);
    };

    reader.readAsDataURL(image);
  };

  useEffect(() => {
    setShouldPreviewUpdate(false);
  }, [shouldPreviewUpdate]);

  useEffect(() => {
    setShouldPictureNameUpdate(false);
  }, [shouldPictureNameUpdate]);

  useEffect(() => {
    setShouldPageUpdate(false);
  }, [shouldPageUpdate]);

  useEffect(() => {
    fetchConfigs();

    return () => {
      setIsUnmounted(true);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Typography variant="body2" marginTop={2} marginBottom={2}>
        注意事項: 圖檔只可上傳JPG.PNG，圖檔尺寸限制1067*600，大小限制20MB
      </Typography>
      {!shouldPageUpdate ? (
        <Box ref={configForm} component="form" noValidate onSubmit={onSubmit}>
          <Paper
            sx={{
              width: '100%',
              overflow: 'hidden',
              padding: 1,
              marginBottom: 2,
            }}
            elevation={5}
          >
            <TableContainer sx={{ maxHeight: 440 }}>
              <Table stickyHeader aria-label="sticky table">
                <TableHead>
                  <TableRow
                    sx={{
                      display: { xs: 'none', sm: 'table-row' },
                    }}
                  >
                    {configs && columns
                      ? columns.map((column, columnIndex) => (
                          <TableCell
                            key={`${column.id}-${columnIndex}`}
                            align={column.align || 'center'}
                            sx={{
                              minWidth: column.minWidth || 'none',
                              padding: 1,
                              maxWidth: '100%',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {column.label}
                          </TableCell>
                        ))
                      : undefined}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {configs
                    ? configs.map((row) => {
                        return (
                          <TableRow
                            hover
                            role="checkbox"
                            tabIndex={-1}
                            key={`${row.memberID}-${row.id}`}
                            className={classes.root}
                          >
                            {configs && columns
                              ? columns.map((column, columnIndex) => {
                                  return (
                                    <TableCell
                                      key={`${column.id}-${columnIndex}`}
                                      align={column.align || 'center'}
                                      sx={{
                                        display: {
                                          xs: 'none',
                                          sm: 'table-cell',
                                        },
                                        padding: 1,
                                        minHeight: '0',
                                        maxWidth: '100%',
                                        whiteSpace: 'nowrap',
                                      }}
                                    >
                                      {column.id === 'function' ? (
                                        <Grid container spacing={1}>
                                          <Grid item xs={6}>
                                            <Button
                                              type="button"
                                              fullWidth
                                              variant="contained"
                                              color="success"
                                              startIcon={<ImageIcon />}
                                              sx={{ color: 'white' }}
                                              disabled={mode !== MODE.EDIT}
                                              onClick={() =>
                                                setUploadImageConfigOrder(
                                                  row.order
                                                )
                                              }
                                            >
                                              上傳
                                            </Button>
                                          </Grid>
                                          <Grid item xs={6}>
                                            <ToggleButton
                                              name={`is-display-${row.id}`}
                                              value="display"
                                              type="button"
                                              fullWidth
                                              variant="contained"
                                              checkedText="停止"
                                              uncheckedText="顯示"
                                              checkedColor="error"
                                              uncheckedColor="primary"
                                              checkedIcon={
                                                <VisibilityOffIcon />
                                              }
                                              unCheckedIcon={<VisibilityIcon />}
                                              initChecked={row.display}
                                              locked={mode !== MODE.EDIT}
                                            />
                                          </Grid>
                                        </Grid>
                                      ) : (
                                        <>
                                          {column.id === 'pictureName' ? (
                                            !shouldPictureNameUpdate ? (
                                              <VariableTextField
                                                mode={
                                                  column.id === 'order' ||
                                                  column.id === 'pictureName'
                                                    ? MODE.VIEW
                                                    : mode
                                                }
                                                noLabel
                                                name={`${column.id}-${row.id}`}
                                                text={row[column.id]}
                                                textAlign="center"
                                              />
                                            ) : undefined
                                          ) : (
                                            <VariableTextField
                                              mode={
                                                column.id === 'order' ||
                                                column.id === 'pictureName'
                                                  ? MODE.VIEW
                                                  : mode
                                              }
                                              noLabel
                                              name={`${column.id}-${row.id}`}
                                              text={row[column.id]}
                                              textAlign="center"
                                            />
                                          )}
                                        </>
                                      )}
                                    </TableCell>
                                  );
                                })
                              : undefined}

                            <TableCell
                              sx={{
                                display: {
                                  xs: 'block',
                                  sm: 'none',
                                  width: '100%',
                                },
                              }}
                            >
                              <CellItemContainer
                                container
                                borderTop="1px solid"
                                fontWeight="bold"
                              >
                                <CellItemGrid item xs={5}>
                                  版位位置
                                </CellItemGrid>
                                <CellItemGrid item xs={7}>
                                  活動名稱
                                </CellItemGrid>
                              </CellItemContainer>
                              <CellItemContainer container>
                                <CellItemGrid item xs={5}>
                                  <VariableTextField
                                    mode={MODE.VIEW}
                                    noLabel
                                    name={`order-${row.id}`}
                                    text={row.order}
                                    textAlign="center"
                                  />
                                </CellItemGrid>
                                <CellItemGrid item xs={7}>
                                  <VariableTextField
                                    mode={mode}
                                    noLabel
                                    name={`title-${row.id}`}
                                    text={row.title}
                                    textAlign="center"
                                  />
                                </CellItemGrid>
                              </CellItemContainer>
                              <CellItemContainer container fontWeight="bold">
                                <CellItemGrid item xs={5}>
                                  連結網址
                                </CellItemGrid>
                                <CellItemGrid item xs={7}>
                                  目前圖檔
                                </CellItemGrid>
                              </CellItemContainer>
                              <CellItemContainer container>
                                <CellItemGrid item xs={5}>
                                  <VariableTextField
                                    mode={mode}
                                    noLabel
                                    name={`link-${row.id}`}
                                    text={row.link}
                                    textAlign="center"
                                  />
                                </CellItemGrid>
                                <CellItemGrid item xs={7}>
                                  {!shouldPictureNameUpdate ? (
                                    <VariableTextField
                                      mode={MODE.VIEW}
                                      noLabel
                                      name={`pictureName-${row.id}`}
                                      text={row.pictureName}
                                      textAlign="center"
                                    />
                                  ) : undefined}
                                </CellItemGrid>
                              </CellItemContainer>
                              <CellItemContainer container fontWeight="bold">
                                <CellItemGrid item xs={12}>
                                  功能
                                </CellItemGrid>
                              </CellItemContainer>
                              <CellItemContainer container>
                                <Grid
                                  item
                                  container
                                  xs={12}
                                  spacing={1}
                                  padding={1}
                                  borderBottom="1px solid"
                                  borderRight="1px solid"
                                  justifyContent="center"
                                  margin="0"
                                  paddingTop="0"
                                >
                                  <Grid item xs={12}>
                                    <Button
                                      type="button"
                                      fullWidth
                                      variant="contained"
                                      color="success"
                                      startIcon={<ImageIcon />}
                                      sx={{
                                        color: 'white',
                                        maxWidth: '180px',
                                      }}
                                      disabled={mode !== MODE.EDIT}
                                      size="small"
                                      onClick={() =>
                                        setUploadImageConfigOrder(row.order)
                                      }
                                    >
                                      上傳
                                    </Button>
                                  </Grid>
                                  <Grid item xs={12}>
                                    <ToggleButton
                                      id={`is-display-${row.id}`}
                                      name={`is-display-${row.id}`}
                                      value="display"
                                      type="button"
                                      fullWidth
                                      variant="contained"
                                      checkedText="停止"
                                      uncheckedText="顯示"
                                      checkedColor="error"
                                      uncheckedColor="primary"
                                      checkedIcon={<VisibilityOffIcon />}
                                      unCheckedIcon={<VisibilityIcon />}
                                      initChecked={row.display}
                                      locked={mode !== MODE.EDIT}
                                      sx={{ maxWidth: '180px' }}
                                      size="small"
                                    />
                                  </Grid>
                                </Grid>
                              </CellItemContainer>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    : undefined}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          <Grid
            container
            spacing={3}
            sx={{ margin: 'auto', width: '100%', justifyContent: 'center' }}
          >
            {
              <>
                <Button
                  type="submit"
                  variant="contained"
                  color={mode === MODE.EDIT ? 'success' : 'primary'}
                  sx={{ minWidth: '160px', maxWidth: '80%', color: 'white' }}
                  startIcon={mode === MODE.EDIT ? <SaveIcon /> : <EditIcon />}
                >
                  {mode === MODE.EDIT ? '儲存' : '編輯'}
                </Button>
              </>
            }
          </Grid>

          <Dialog
            open={!!uploadImageConfigOrder}
            title="上傳圖片"
            onClose={clearUploadStatus}
            onCancel={clearUploadStatus}
            onConfirm={() => updateUploadData(uploadImageConfigOrder)}
            isConfirm
          >
            <>
              <Box
                mb={2}
                display="flex"
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
              >
                {!shouldPreviewUpdate ? (
                  importedFiles && importedFiles.length > 0 ? (
                    <UploadedImagePreview
                      image={importedFiles[0]}
                      onDelete={() => deleteStoredImage()}
                    />
                  ) : mode === MODE.EDIT ? (
                    <UploadedImagePreview
                      image={configs ? configs.pictureString : ''}
                      onDelete={() => deleteStoredImage()}
                    />
                  ) : undefined
                ) : undefined}
              </Box>
              <Box
                {...getRootProps()}
                sx={{
                  minWidth: '240px',
                  width: { xs: '100%', sm: '360px' },
                  height: '200px',
                  border: '1px dashed #bbbbbb',
                  padding: 3,
                  borderRadius: 2,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  marginLeft: 'auto',
                  marginRight: 'auto',
                  color: '#777777',
                  marginBottom: 2,
                }}
              >
                <input
                  ref={uploader}
                  id="uploadedImage"
                  name="uploadedImage"
                  {...getInputProps()}
                />
                {isDragActive ? (
                  <Box>放開以上傳照片</Box>
                ) : (
                  <>
                    <Box className="text-center">
                      <AddAPhotoIcon
                        sx={{
                          fontSize: { xs: 60, sm: 72 },
                          color: '#aaaaaa',
                          mb: 1,
                        }}
                      />
                      <Box>
                        {importedFiles && importedFiles.length > 0
                          ? '重新上傳照片'
                          : '新增照片'}
                        <Typography variant="body2">
                          (點擊或拖曳照片至此)
                        </Typography>
                      </Box>
                    </Box>
                  </>
                )}
              </Box>
              <Typography variant="body2" color="error">
                {uploadErrorMessage}
              </Typography>
            </>
          </Dialog>
        </Box>
      ) : undefined}
    </>
  );
};

export default HomepageConfigPage;
